// Mic -> AudioWorklet (PCM capture + mute) -> WebCodecs AudioEncoder (Opus).
//
// The worklet (public/streaming/audio-capture.worklet.js) posts mono Float32
// buffers; we batch them to ~20 ms AudioData and encode. Mute keeps encoding
// zeroed PCM so the muxed timeline never gaps. Supports hot mic replacement
// (device switch while live) — the encoder and its timeline continue.

export const AUDIO_BITRATE = 96_000;
const WORKLET_URL = "/streaming/audio-capture.worklet.js";
const BATCH_FRAMES = 960; // 20ms @ 48kHz

export async function createAudioPipeline({ stream, onChunk, onRawAudio, onError }) {
  const track = stream.getAudioTracks()[0];
  if (!track) throw new Error("no audio track");

  const ctx = new AudioContext({ sampleRate: 48000 });
  // iOS starts contexts suspended; we're inside a user-gesture call chain here.
  if (ctx.state === "suspended") await ctx.resume().catch(() => {});
  const sampleRate = ctx.sampleRate;

  await ctx.audioWorklet.addModule(WORKLET_URL);
  const worklet = new AudioWorkletNode(ctx, "el-capture", {
    numberOfInputs: 1,
    numberOfOutputs: 0,
  });

  let source = ctx.createMediaStreamSource(new MediaStream([track]));
  source.connect(worklet);

  const encoder = new AudioEncoder({
    output: (chunk, meta) => onChunk(chunk, meta),
    error: (e) => onError?.(e),
  });
  encoder.configure({
    codec: "opus",
    sampleRate,
    numberOfChannels: 1,
    bitrate: AUDIO_BITRATE,
  });

  let pending = new Float32Array(BATCH_FRAMES);
  let pendingLen = 0;
  let totalFrames = 0;
  let running = true;

  worklet.port.onmessage = ({ data }) => {
    if (!running || !(data instanceof Float32Array)) return;
    let offset = 0;
    while (offset < data.length) {
      const n = Math.min(data.length - offset, BATCH_FRAMES - pendingLen);
      pending.set(data.subarray(offset, offset + n), pendingLen);
      pendingLen += n;
      offset += n;
      if (pendingLen === BATCH_FRAMES) {
        const audioData = new AudioData({
          format: "f32-planar",
          sampleRate,
          numberOfFrames: BATCH_FRAMES,
          numberOfChannels: 1,
          timestamp: Math.round((totalFrames / sampleRate) * 1e6),
          data: pending,
        });
        if (encoder.state === "configured" && encoder.encodeQueueSize < 8) {
          encoder.encode(audioData);
        }
        onRawAudio?.(audioData); // local recorder tap (copies internally)
        audioData.close();
        totalFrames += BATCH_FRAMES;
        pending = new Float32Array(BATCH_FRAMES);
        pendingLen = 0;
      }
    }
  };

  return {
    sampleRate,
    setMuted(muted) {
      worklet.port.postMessage({ muted: !!muted });
    },
    // Mic switch while live: swap the source node; encoder timeline continues.
    replaceTrack(newTrack) {
      try {
        source.disconnect();
      } catch {}
      source = ctx.createMediaStreamSource(new MediaStream([newTrack]));
      source.connect(worklet);
    },
    async stop() {
      running = false;
      worklet.port.onmessage = null;
      try {
        source.disconnect();
        worklet.disconnect();
      } catch {}
      try {
        if (encoder.state === "configured") await encoder.flush();
      } catch {}
      try {
        encoder.close();
      } catch {}
      await ctx.close().catch(() => {});
    },
  };
}

// Standalone VU-meter tap, independent of the encode pipeline so it works in
// preview too. Stereo when the mic delivers 2 channels (both bars mirror a
// mono source). Chrome/Safari allow AudioContexts to run while a live
// getUserMedia capture is active, so this works without an extra gesture.
export function createLevelMonitor(stream) {
  const ctx = new AudioContext();
  const splitter = ctx.createChannelSplitter(2);
  const analysers = [ctx.createAnalyser(), ctx.createAnalyser()];
  analysers.forEach((a, i) => {
    a.fftSize = 1024;
    splitter.connect(a, i);
  });
  const buf = new Float32Array(1024);
  let source = null;
  let channels = 1;

  const attach = (nextStream) => {
    const track = nextStream.getAudioTracks()[0];
    if (!track) return;
    channels = track.getSettings().channelCount || 1;
    try {
      source?.disconnect();
    } catch {}
    source = ctx.createMediaStreamSource(new MediaStream([track]));
    source.connect(splitter);
  };
  attach(stream);

  const rms = (analyser) => {
    analyser.getFloatTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
    return Math.min(1, Math.sqrt(sum / buf.length) * 4); // speech ≈ mid-meter
  };

  return {
    attach,
    getLevels() {
      if (ctx.state === "suspended") ctx.resume().catch(() => {});
      const left = rms(analysers[0]);
      return [left, channels > 1 ? rms(analysers[1]) : left];
    },
    close() {
      try {
        source?.disconnect();
      } catch {}
      ctx.close().catch(() => {});
    },
  };
}
